import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { Group } from "@mapmycustomers/shared/types/entity";

import localSettings from "@app/config/LocalSettings";
import { isEntityFrequencyUpdating, isFrequencyPanelLoading } from "@app/store/frequency";
import {
  initFrequencyPanel,
  setFrequencyPanelConfig,
  updateEntityFrequency,
} from "@app/store/frequency/actions";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useToggleBooleanAfterTimeout from "@app/util/hook/useToggleBooleanAfterTimeout";

import FrequencyBox from "../FrequencyBox";
import FrequencyCollapsePanel from "../FrequencyCollapsePanel";
import FrequencyEntity from "../type/FrequencyEntity";
import hasFrequency from "../util/hasFrequency";

import FrequencySelectorWithConfig from "./component/FrequencySelectorWithConfig";
import FrequencyState from "./component/FrequencyState";
import GroupFrequencyState from "./component/GroupFrequencyState";
import Loader from "./component/Loader";
import styles from "./FrequencyPanel.module.scss";
import FrequencyContext from "./util/FrequencyContext";
import useFrequencyPreviewConfig from "./util/useFrequencyPreviewConfig";

interface Props {
  entity: FrequencyEntity;
  groups?: Group[];
  isEntityFrequencyUpdating: boolean;
  isGroup?: boolean;
  isReadOnlyMember: boolean;
  onInitFrequencyPanel: typeof initFrequencyPanel.request;
  onUpdate: () => void;
  onViewActivityHistory?: () => void;
  panelLoading: boolean;
  setFrequencyPanelConfig: typeof setFrequencyPanelConfig;
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
  updateEntityFrequency: typeof updateEntityFrequency.request;
}

const FrequencyPanel: React.FC<Props> = ({
  entity,
  groups,
  isEntityFrequencyUpdating,
  isGroup,
  isReadOnlyMember,
  onInitFrequencyPanel,
  onUpdate,
  onViewActivityHistory,
  panelLoading,
  setFrequencyPanelConfig,
  subscribe,
  unsubscribe,
  updateEntityFrequency,
}) => {
  const intl = useIntl();

  const config = useFrequencyPreviewConfig(entity, isGroup, groups);
  const [entityIdType, setEntityIdType] = useState<string>("");
  const [entityDoesHaveFrequencyAlready, setEntityDoesHaveFrequencyAlready] =
    useState<boolean>(false);
  const analytics = useExtendedAnalytics("Set a Frequency", useAnalytics());

  useEffect(() => {
    const value = `${entity.entity}_${entity.id}`;
    if (value !== entityIdType) {
      setEntityIdType(value);
      setEntityDoesHaveFrequencyAlready(hasFrequency(entity) || !!groups?.some(hasFrequency));
    }
  }, [groups, entity, entityIdType, setEntityIdType, setEntityDoesHaveFrequencyAlready]);

  const showLoading = useToggleBooleanAfterTimeout(1000, false, panelLoading, true);

  useEffect(() => {
    setFrequencyPanelConfig(config);
    onInitFrequencyPanel();
  }, [config, onInitFrequencyPanel, setFrequencyPanelConfig]);

  const handleUpdateFrequency = useCallback(
    (cadence: Partial<Cadenced>) => {
      if (entity) {
        updateEntityFrequency({
          callback: onUpdate,
          entity: {
            ...entity,
            ...cadence,
          },
        });
      }
    },
    [entity, onUpdate, updateEntityFrequency]
  );

  useEffect(() => {
    const listener = {
      onUpdate,
    };

    subscribe({ entityTypes: [entity.entity], listener });

    return () => {
      unsubscribe({ entityTypes: [entity.entity], listener });
    };
  }, [entity.entity, onUpdate, subscribe, unsubscribe]);

  const contextValue = useMemo(
    () => ({
      entityDoesHaveFrequencyAlready,
      isEntityFrequencyUpdating,
      onUpdate,
      onViewActivityHistory,
    }),
    [entityDoesHaveFrequencyAlready, isEntityFrequencyUpdating, onUpdate, onViewActivityHistory]
  );

  const handleOpenSetFrequencyBlock = useCallback(() => {
    analytics.clicked([]);
    localSettings.setFrequencyPanelOpenState(true);
  }, [analytics]);

  const handleCloseSetFrequencyBlock = useCallback(() => {
    localSettings.setFrequencyPanelOpenState(false);
  }, []);

  if (isReadOnlyMember) {
    return null;
  }

  return (
    <FrequencyContext.Provider value={contextValue}>
      <AnalyticsContext.Provider value={analytics}>
        <FrequencyBox>
          <div className={cn(styles.wrapper)}>
            {showLoading ? (
              <Loader />
            ) : config.isSomeGroupFrequencied || config.isEntityFrequencied ? (
              isGroup ? (
                <GroupFrequencyState />
              ) : (
                <FrequencyState onFrequencyChange={handleUpdateFrequency} />
              )
            ) : (
              <FrequencyCollapsePanel
                header={intl.formatMessage({
                  id: "frequency.cadenceSelector.info.setFrequency",
                  defaultMessage: "Set a Frequency",
                  description: "frequency component - Set a Frequency",
                })}
                isOpenByDefault={localSettings.getFrequencyPanelOpenState()}
                onClose={handleCloseSetFrequencyBlock}
                onOpen={handleOpenSetFrequencyBlock}
              >
                <FrequencySelectorWithConfig onChange={handleUpdateFrequency} />
              </FrequencyCollapsePanel>
            )}
          </div>
        </FrequencyBox>
      </AnalyticsContext.Provider>
    </FrequencyContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  isEntityFrequencyUpdating: isEntityFrequencyUpdating(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  panelLoading: isFrequencyPanelLoading(state),
});

const mapDispatchToProps = {
  onInitFrequencyPanel: initFrequencyPanel.request,
  setFrequencyPanelConfig,
  subscribe,
  unsubscribe,
  updateEntityFrequency: updateEntityFrequency.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(FrequencyPanel);
